import qs from 'qs';
import type { MyTraderaPurchaseOrderApiDto } from 'tradera/state/services/types/webapi-mytradera-generated';

type MessagingThreadLinkOptions = {
    /**
     * Open in single thread mode
     */
    singleThread?: boolean;
    /**
     * Open message form right away
     */
    openForm?: boolean;
    /**
     * Message source info for tracking
     */
    source?: string;
    traderaUserId?: number;
};

/**
 * Returns base path for a thread
 */
export const getMessagingThreadLink = (
    u1: number,
    u2: number,
    l: number,
    options?: MessagingThreadLinkOptions
): string => {
    const optionsWithDefaults = {
        singleThread: false,
        openForm: false,
        ...options
    };
    const { singleThread, ...queryParams } = optionsWithDefaults;
    return (
        (singleThread ? '/thread/' : '/threads/') +
        'u' +
        Math.min(u1, u2) +
        '_u' +
        Math.max(u1, u2) +
        '_l' +
        l +
        qs.stringify(queryParams, { addQueryPrefix: true })
    );
};

/**
 * Returns the path to a thread on Tradera's messaging page
 */
export const getMessagingPageLink = (
    u1: number,
    u2: number,
    l: number,
    options?: MessagingThreadLinkOptions
) =>
    'messaging' +
    (u1 && u2 && l ? getMessagingThreadLink(u1, u2, l, options) : '');

/**
 * Returns base path for a thread for a purchase order
 */
const getCsMessagingPageThreadLinkForPurchaseOrder = (
    purchaseOrder: MyTraderaPurchaseOrderApiDto,
    memberId?: number,
    options?: MessagingThreadLinkOptions
): string | null => {
    const optionsWithDefaults = {
        singleThread: false,
        openForm: false,
        ...options
    };
    const { singleThread, ...queryParams } = optionsWithDefaults;

    const itemIds = purchaseOrder.rows
        ?.map((po) => po.listing?.itemId)
        .filter((id) => id !== undefined);

    if (!memberId || !itemIds || itemIds.length === 0) {
        return null;
    }

    const itemId = Math.min(...itemIds);

    return (
        (singleThread ? '/thread/' : '/threads/') +
        'xcs_u' +
        memberId +
        '_l' +
        itemId +
        qs.stringify(queryParams, { addQueryPrefix: true })
    );
};

const getCsMessagingPageThreadLinkForPurchaseOrderForBuyer = (
    purchaseOrder: MyTraderaPurchaseOrderApiDto,
    options?: MessagingThreadLinkOptions
) =>
    getCsMessagingPageThreadLinkForPurchaseOrder(
        purchaseOrder,
        purchaseOrder.buyerMember?.memberId,
        options
    );

export const getCsMessagingPageThreadLinkForPurchaseOrderForSeller = (
    purchaseOrder: MyTraderaPurchaseOrderApiDto,
    options?: MessagingThreadLinkOptions
) =>
    getCsMessagingPageThreadLinkForPurchaseOrder(
        purchaseOrder,
        purchaseOrder.sellerMember?.memberId,
        options
    );

export const getCsMessagingPageLinkForPurchaseOrderForBuyer = (
    purchaseOrder: MyTraderaPurchaseOrderApiDto,
    options?: MessagingThreadLinkOptions
) => {
    const threadLink = getCsMessagingPageThreadLinkForPurchaseOrderForBuyer(
        purchaseOrder,
        options
    );
    return threadLink ? 'messaging' + threadLink : null;
};

export const getCsMessagingPageLinkForPurchaseOrderForSeller = (
    purchaseOrder: MyTraderaPurchaseOrderApiDto,
    options?: MessagingThreadLinkOptions
) => {
    const threadLink = getCsMessagingPageThreadLinkForPurchaseOrderForSeller(
        purchaseOrder,
        options
    );
    return threadLink ? 'messaging' + threadLink : null;
};
